import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import { fetchRegistrationAppointmentWeek } from "../../Appointment/registrationAppointmentTimeslots";
import { mapRdProfileApiToView } from "../registrationMatchedRd";
import RegistrationRdAvatar from "../RegistrationRdAvatar";
import {
  clearNoPreferenceRegistrationState,
  getRegistrationData,
  isCardOnFileComplete,
  updateRegistrationData,
} from "../../registrationSetupStorage";
import { registrationGoBack } from "../../registrationNavigation";
import {
  trackRegistrationError,
  trackRegistrationStepCompleted,
} from "../../../../helper/registrationAnalytics";

const PROFILE_STEP = REGISTRATION_SETUP_STEP_META.DIETITIAN_PROFILE;

const RegistrationDietitianProfile = ({ history, match }) => {
  const { t, i18n } = useTranslation();
  const rdId = match.params.rdId;
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBooking, setIsBooking] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    const data = getRegistrationData();
    if (!isCardOnFileComplete(data)) {
      history.replace("/register/account/card");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history]);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      if (!rdId) {
        history.replace("/register/account/dietitians");
        return;
      }

      setIsLoading(true);
      try {
        const res = await AuthenticationApiSerivce.getRegistrationRdProfile(
          rdId,
          i18n.language
        );

        if (cancelled) {
          return;
        }

        if (!res?.status) {
          message.error(res?.message || t("NUTRITION_RDPROFILE_ERR_LOAD"));
          history.replace("/register/account/dietitians");
          return;
        }

        const mapped = mapRdProfileApiToView(res?.data ?? res);
        if (!mapped) {
          message.error(t("NUTRITION_RDPROFILE_ERR_UNAVAILABLE"));
          history.replace("/register/account/dietitians");
          return;
        }

        setProfile(mapped);
        updateRegistrationData({
          selectedRdProfile: {
            rdId: String(rdId),
            raw: res?.data ?? res,
            view: mapped,
          },
        });
      } catch (err) {
        if (!cancelled) {
          message.error(
            err?.response?.data?.message ||
              err?.message ||
              t("NUTRITION_RDPROFILE_ERR_LOAD")
          );
          history.replace("/register/account/dietitians");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [history, rdId, i18n.language]);

  const handleBook = async () => {
    if (!profile || isBooking) {
      return;
    }

    setIsBooking(true);
    try {
      const timeslotResult = await fetchRegistrationAppointmentWeek({
        rdId,
        rawProfile: profile.raw,
        language: i18n.language,
        apiService: AuthenticationApiSerivce,
      });

      updateRegistrationData({
        ...clearNoPreferenceRegistrationState(),
        selectedDietitian: {
          id: rdId,
          name: profile.name,
          noPreference: false,
          raw: profile.raw,
        },
        appointmentTimeslotRequest: timeslotResult.payload,
        rdTimeslots: timeslotResult.rdSlot,
        appointmentWeek: timeslotResult.appointmentWeek,
        selectedAppointment: null,
      });
      trackRegistrationStepCompleted("provider", 5);
      history.push("/register/account/appointments");
    } catch (err) {
      trackRegistrationError({
        stepName: "provider",
        stepNumber: 5,
        errorType: "api_failed",
      });
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          t("NUTRITION_RDPROFILE_ERR_LOAD_TIMES")
      );
    } finally {
      setIsBooking(false);
    }
  };

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={PROFILE_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={() => registrationGoBack(history)}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card content-card-profile">
            <div className="content-card-scroll">
              {isLoading ? (
                <p className="section-sub">{t("NUTRITION_RDPROFILE_LBL_LOADING")}</p>
              ) : profile ? (
                <>
                  <div className="rd-profile-hero">
                    <RegistrationRdAvatar
                      className="rd-profile-avatar"
                      profileImage={profile.profileImage}
                      initials={profile.initials}
                      avatarStyle={profile.avatarStyle}
                      alt={profile.name}
                    />
                    <div>
                      <div className="rd-profile-name">{profile.name}</div>
                      {profile.creds ? (
                        <div className="rd-profile-creds">{profile.creds}</div>
                      ) : null}
                      <div className="rd-profile-badge">
                        <span aria-hidden>✓</span>{" "}
                        {t("NUTRITION_RDPROFILE_LBL_ACCEPTING_PATIENTS")}
                      </div>
                    </div>
                  </div>

                  {profile.about ? (
                    <div className="rd-detail-section">
                      <div className="rd-detail-label">
                        {t("NUTRITION_RDPROFILE_LBL_ABOUT")}
                      </div>
                      <div className="rd-detail-text">{profile.about}</div>
                    </div>
                  ) : null}

                  {profile.specialties.length > 0 ? (
                    <div className="rd-detail-section">
                      <div className="rd-detail-label">
                        {t("NUTRITION_RDPROFILE_LBL_SPECIALTIES")}
                      </div>
                      <div className="rd-detail-categories">
                        {profile.specialties.map((item) => (
                          <span key={item} className="rd-detail-category">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {profile.licensedIn.length > 0 ? (
                    <div
                      className={`rd-detail-section${
                        !profile.approach ? " rd-detail-section-last" : ""
                      }`}
                    >
                      <div className="rd-detail-label">
                        {t("NUTRITION_RDPROFILE_LBL_LICENSED_IN")}
                      </div>
                      <div className="rd-detail-chips">
                        {profile.licensedIn.map((stateCode) => (
                          <span
                            key={stateCode}
                            className="rd-detail-chip rd-detail-chip-state"
                          >
                            {stateCode}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {profile.approach ? (
                    <div className="rd-detail-section rd-detail-section-last">
                      <div className="rd-detail-label">
                        {t("NUTRITION_RDPROFILE_LBL_APPROACH")}
                      </div>
                      <div className="rd-detail-text">{profile.approach}</div>
                    </div>
                  ) : null}

                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleBook}
                    disabled={isBooking}
                  >
                    {isBooking ? `${t("LOADING")}...` : t("NUTRITION_RDPROFILE_BTN_BOOK")}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => history.push("/register/account/dietitians")}
                  >
                    {t("NUTRITION_RDPROFILE_BTN_VIEW_ALL")}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </RegistrationLayout>
    </RegistrationSetupWrapper>
  );
};

export default withRouter(RegistrationDietitianProfile);
